import React from 'react';

/**
 * Surface container. `interactive` adds the hover lift used by anything that
 * navigates on click — pair it with an onClick and the keyboard props below.
 */
const Card = React.forwardRef(function Card(
  { interactive = false, selected = false, className = '', children, onClick, ...rest },
  ref
) {
  const clickable = interactive && typeof onClick === 'function';

  return (
    <div
      ref={ref}
      onClick={onClick}
      // A clickable div still has to be reachable and activatable by keyboard.
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      className={[
        'rounded-lg border bg-surface shadow-sm',
        'transition-all duration-base ease-out',
        selected ? 'border-accent ring-2 ring-accent/30' : 'border-border',
        clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-border-strong' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
});

export const CardHeader = ({ title, subtitle, actions, className = '', children }) => (
  <div className={`flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3 sm:px-5 ${className}`}>
    {children || (
      <div className="min-w-0">
        {title && <h3 className="text-base font-semibold text-foreground">{title}</h3>}
        {subtitle && <p className="mt-0.5 text-sm text-muted-fg">{subtitle}</p>}
      </div>
    )}
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const CardBody = ({ className = '', children }) => (
  <div className={`px-4 py-4 sm:px-5 ${className}`}>{children}</div>
);

export const CardFooter = ({ className = '', children }) => (
  <div className={`flex flex-wrap items-center gap-2 border-t border-border bg-muted/50 px-4 py-3 sm:px-5 ${className}`}>
    {children}
  </div>
);

export default Card;
